import React, { useEffect, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import { DataGrid, GridPagination } from "@mui/x-data-grid";
import { CircularProgress, IconButton, Dialog, DialogActions, DialogContent, DialogTitle, Button, Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import { FaPlusCircle } from "react-icons/fa";
import Toolbar from "../Toolbar/Toolbar.js";
import './DualSenseTable.css';
import apiService from "../../API/ApiService.js";
import { useLocation } from 'react-router-dom';

const DualSenseTable = ({ filteredData, onDualSenseClick, totalCount, appliedFilters }) => {
  const [rows, setRows] = useState([]);
  const [totalRowCount, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [columns, setColumns] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([
    'realSerialNumber', 
    'globalStatus',
    'raspberry_id', 
    'deviceSerialNumber',
    'job_id',
    'symptome_description',
    'grade',
    'startDate'
  ]);
  const [openDialog, setOpenDialog] = useState(false);

  const location = useLocation();
  const { username } = location.state || {};

  const [storedUsername, setStoredUsername] = useState('');

  useEffect(() => {
    if (username) {
      setStoredUsername(username);
    }
  }, [username]);

  const generateColumns = useCallback((data) => {
    if (!data || data.length === 0) return [];

    return Object.keys(data[0])
      .filter(key => !['id', 'userId', 'MODEL_NO', 'symptome_code', 'code_ds5_id', 
                     'final_status', 'process_outcome', 
                     'processed_date', 'duration', 'raspberry_serialnumber', 'isActive'].includes(key))
      .map(key => {
        let headerName = key.replace(/_/g, ' ').toUpperCase();
        
        if (key === 'raspberry_id') headerName = 'TESTER NAME';
        if (key === 'realSerialNumber') headerName = 'DS5_SN';
        if (key === 'globalStatus') headerName = 'Status';
        if (key === 'deviceSerialNumber') headerName = 'Mac@';
        if (key === 'symptome_description') headerName = 'Symptom';
        
        return {
          field: key,
          headerName,
          flex: 1,
          minWidth: 120,
          maxWidth: 300,
          renderCell: (params) => (
            <Tooltip title={params.value || ''} arrow>
              <div style={{
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {params.value}
              </div>
            </Tooltip>
          )
        };
      });
  }, []);

  const fetchData = useCallback(async (newPage, newPageSize, filters = appliedFilters) => {
    setLoading(true);
    setError(null);
  
    try {
      const params = {
        page: newPage + 1,
        pageSize: newPageSize === -1 ? totalRowCount : newPageSize,
        ...filters,
        date: filters.dateRange,
      };

      const trimmedUsername = username?.trim() || '';
      let apiEndpoint = '/';
  
      if (trimmedUsername === "GL_EU") {
        apiEndpoint = '/?username=GL_EU'; 
      } else if (trimmedUsername === "GL_US") {
        apiEndpoint = '/?username=GL_US'; 
      }
      else if (trimmedUsername === "SONY_DS5") {
        apiEndpoint = '/?username=SONY_DS5'; 
      }
      else if (trimmedUsername === "NWT_DS5") {
        apiEndpoint = '/?username=NWT_DS5'; 
      }
        else if (trimmedUsername === "NWT_DS5") {
        apiEndpoint = '/?username=NWT_DS5'; 
      }

      const response = await apiService.get(apiEndpoint, { params });
      console.log("dualsensetable is ",response.data)
      if (response?.data?.data) {
        setRows(response.data.data);
        setTotalRows(response.data.totalCount);
        setColumns(generateColumns(response.data.data)); 
      }
    } catch (error) {
      setError("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, [generateColumns, username, appliedFilters, totalRowCount]);

  useEffect(() => {
    if (filteredData?.length > 0) {
      setRows(filteredData);
      setColumns(generateColumns(filteredData));
      setTotalRows(totalCount);
    } else {
      fetchData(page, pageSize, appliedFilters);
    }
  }, [filteredData, generateColumns, page, pageSize, totalCount, appliedFilters, fetchData]);

  const toggleColumnSelectionDialog = useCallback(() => {
    setOpenDialog(prev => !prev);
  }, []);

  const handleColumnVisibilityChange = useCallback((columnField) => {
    setVisibleColumns(prev => 
      prev.includes(columnField) 
        ? prev.filter(col => col !== columnField) 
        : [...prev, columnField]
    );
  }, []);

  const sortedColumns = columns
    .filter(col => visibleColumns.includes(col.field))
    .sort((a, b) => visibleColumns.indexOf(a.field) - visibleColumns.indexOf(b.field));

  const handlePaginationModelChange = (newModel) => {
    const { pageSize: newPageSize, page: newPage } = newModel;
    setPage(newPage);
    setPageSize(newPageSize);
    fetchData(newPage, newPageSize, appliedFilters);
  };

  return (
    <Box sx={{ height: "100%", width: "100%", display: 'flex', flexDirection: 'column' }}>
      <Toolbar
        data={rows}
        visibleColumns={visibleColumns}
        filename="DS5DataExtraction"
        filters={appliedFilters}
      />

      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        <Box sx={{ position: 'absolute', right: 16, top: 8, zIndex: 1 }}>
          <IconButton onClick={toggleColumnSelectionDialog}>
            <FaPlusCircle size={24} />
          </IconButton>
        </Box>

        <Dialog open={openDialog} onClose={toggleColumnSelectionDialog}>
          <DialogTitle>Select Columns to Display</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {columns.map(col => (
                <FormControlLabel
                  key={col.field}
                  control={
                    <Checkbox 
                      checked={visibleColumns.includes(col.field)} 
                      onChange={() => handleColumnVisibilityChange(col.field)} 
                    />
                  }
                  label={col.headerName}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={toggleColumnSelectionDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%' 
          }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            color: 'text.secondary'
          }}>
            No data available for the selected filters
          </Box>
        ) : (
          <DataGrid
            rows={rows}
            columns={sortedColumns}
            pageSizeOptions={[20, 50, 100, { value: -1, label: 'All' }]}
            paginationMode="server"
            paginationModel={{ page, pageSize }}
            rowCount={totalRowCount}
            loading={loading}
            onPaginationModelChange={handlePaginationModelChange}
            disableRowSelectionOnClick
            slots={{
              pagination: GridPagination,
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
                padding: '0 8px',
                lineHeight: '1.2',
              },
              '& .MuiDataGrid-columnHeader': {
                padding: '0 8px',
              },
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                width: '100%',
              },
            }}
            getCellClassName={(params) => {
              if (params.field === 'globalStatus') {
                return params.value === 'OK' ? 'cell-ok' : 
                       params.value === 'KO' ? 'cell-ko' : '';
              }
              return '';
            }}
            onRowClick={(params) => onDualSenseClick(params.row)}
          />
        )}
      </Box>
    </Box>
  );
};

export default DualSenseTable;